import { Component, inject, OnDestroy, OnInit, Renderer2, TemplateRef } from '@angular/core'
import { environment } from '../../../../environments/environment'
import { CommonModule, NgIf, NgIfContext } from '@angular/common'
import { AddSpacePipe } from '../../../pipes/add-space.pipe'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { Store } from '@ngrx/store'
import { logout } from '../../../store/auth/auth.actions'
import { selectUser } from '../../../store/auth/auth.selectors'
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs'
import { AuthState, selectSubscription } from '../../../store/auth/auth.state'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { HttpClient } from '@angular/common/http'
import { ApiService } from '../../../services/api.service'
import { AuthService } from '../../../services/auth.service'
import { Router, RouterModule } from '@angular/router'
import { eps } from '../../../services/url.service'
import { SubscriptionStatus } from '../../../services/subscriptions'
import { User } from '../../../store/user/user.state'
import { loadEmployeeById$ } from '../../../store/user/user.effects'
import { NotificationService } from '../../../services/notification.service'
import { AlertService, AlertType } from '../../../shared/alert.service'

export interface AppNotification {
  id: string
  title: string
  message: string
  createdAt: string | Date
}

@Component({
  selector: 'app-app-header',
  imports: [NgIf, CommonModule, AddSpacePipe, CapitalizePipe, RouterModule],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss'
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  public apiEndpoint = environment.apiBaseUrl
  private router = inject(Router)
  employee$: Observable<User | undefined> = of(undefined) // Initialize with a default value
  employeeImages: { [key: string]: string } = {} // Ensure this is defined correctly
  // public profilePicture = '../../../../assets/img/user.jpg'
  // public businessPicture = '../../../../assets/img/user.jpg'

  subscription$!: Observable<AuthState['subscription']>
  subscriptionStatus: string | null = null
  trialDaysRemaining: number | null = null
  trialMessage: string | null = null
  private destroy$ = new Subject<void>()
  employee: any
  loading: any
  // isOwner: string | boolean | null
  eps = eps
  notifications: AppNotification[] = []
  isNotificationsLoading = false
  private lastAlertKey: string | null = null

  constructor(
    private store: Store,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private apiService: ApiService,
    private authService: AuthService,
    private renderer: Renderer2,
    private notificationService: NotificationService,
    private alertService: AlertService
  ) {
    this.subscription$ = this.store.select(selectSubscription)
    // this.subscription$
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     tap(sub => (this.subscriptionStatus = sub?.subscriptionStatus ?? null))
    //   )
    //   .subscribe()
    this.subscription$
      .pipe(
        takeUntil(this.destroy$),
        tap(sub => {
          this.subscriptionStatus = sub?.subscriptionStatus ?? null

          if (
            sub?.subscriptionStatus === SubscriptionStatus.Trialing &&
            sub.expiryDate &&
            this.isOwner
          ) {
            const today = new Date()
            const trialEnd = new Date(sub.expiryDate)

            // Calculate remaining days
            const remainingDays = Math.max(
              0,
              Math.ceil((trialEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
            )
            this.trialDaysRemaining = remainingDays

            // Update the message to show in HTML
            if (remainingDays > 1 && this.isOwner) {
              this.trialMessage = `You have ${remainingDays} days remaining in your trial.`
              // this.alertService.show(`You have ${remainingDays} days remaining in your trial.`)
              this.triggerOnce('trial-expire', this.trialMessage, 'danger')
            } else if (remainingDays === 1 && this.isOwner) {
              this.trialMessage = `This is the last day of your trial subscription.`
              // this.alertService.show(`This is the last day of your trial subscription.`)
              this.triggerOnce('trial-expire', this.trialMessage, 'danger')
            } else if (this.isOwner) {
              this.trialMessage = `Your trial subscription has expired.`
              // this.alertService.show(`Your trial subscription has expired.`)
              this.triggerOnce('trial-expire', this.trialMessage, 'danger')
            }
          } else if (sub?.trialDays && this.isOwner && sub.trialDays > 0) {
            this.trialMessage = `You have a trial subscription for ${sub.trialDays} days.`
            this.trialDaysRemaining = null
            // this.alertService.show(`You have a trial subscription for ${sub.trialDays} days.`)
            this.triggerOnce('trial-expire', this.trialMessage, 'danger')
          } else if (sub?.trialDays && this.isOwner && sub.trialDays <= 0) {
            this.trialMessage = `Your subscription has expired.`
            this.trialDaysRemaining = null
            // this.alertService.show(`You have a trial subscription for ${sub.trialDays} days.`)
            this.triggerOnce('trial-expire', this.trialMessage, 'danger')
          } else {
            this.trialMessage = null
            this.trialDaysRemaining = null
          }
        })
      )
      .subscribe()
  }

  user: AuthState['user'] | null = null
  userPicture: string | SafeUrl | null = null
  userimageObjectURL!: string | null

  ngOnInit(): void {
    this.store
      .select(selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user ?? null

        if (user?.picture) {
          const imageUrl = `${this.apiEndpoint}/uploads/${user.picture}`

          this.authService.getImage(imageUrl).subscribe({
            next: imageBlob => {
              // revoke old object URL to avoid memory leak
              if (this.userimageObjectURL) {
                URL.revokeObjectURL(this.userimageObjectURL)
              }

              this.userimageObjectURL = URL.createObjectURL(imageBlob)
              this.userPicture = this.userimageObjectURL
              // this.store.dispatch(loadEmployeeById$())
            },
            error: err => {
              console.error('Error fetching image:', err)
              // this.userPicture = this.profilePicture
            }
          })
        } else {
          // this.userPicture = this.profilePicture
        }
      })
    this.store
      .select(selectUser)
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user),
        tap(user => {
          this.user = user ?? null
        }),
        switchMap(user => {
          if (!user) return of([])
          return this.getNotifications(user.id)
        })
      )
      .subscribe(res => {
        this.notifications = res
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()

    if (this.userimageObjectURL) {
      URL.revokeObjectURL(this.userimageObjectURL)
    }
  }

  logout() {
    this.alertService.clear() // ✅ add this
    this.store.dispatch(logout())
  }

  checkImage(url: string): void {
    this.http.get(url, { responseType: 'blob' }).subscribe(
      response => {
        console.log('Image is accessible:', response)
      },
      error => {
        console.error('Image access error:', error)
      }
    )
  }

  getSanitizedImageUrl(imageUrl: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(imageUrl)
  }

  getInitials(firstName?: string | null, lastName?: string | null): string {
    const f = (firstName ?? '').trim()
    const l = (lastName ?? '').trim()
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase()
  }

  // getDashboardRoute(): string {
  //   const isAdmin = this.user?.userRoles?.some(ur => ur.role.name === 'Admin')
  //   return isAdmin ? eps.dashboardPath : eps.employeeDashboardPath
  // }

  getDashboardRoute(): string {
    // Check if any role in userRoles array has the name 'Admin'console.log('user', this.user?.userRoles)
    const isAdmin = this.user?.userRoles?.some(ur => ur.role?.name === 'Admin')
    return isAdmin ? eps.dashboardPath : eps.employeeDashboardPath
  }

  // toggleSidebar(): void {
  //   const mainWrapper = document.querySelector('.main-wrapper')

  //   if (mainWrapper?.classList.contains('slide-nav')) {
  //     this.renderer.removeClass(mainWrapper, 'slide-nav')
  //   } else {
  //     this.renderer.addClass(mainWrapper, 'slide-nav')
  //   }
  // }
  toggleSidebar(event: Event): void {
    event.stopPropagation() // 🔥 prevents HostListener firing

    const mainWrapper = document.querySelector('.main-wrapper')

    if (mainWrapper?.classList.contains('slide-nav')) {
      this.renderer.removeClass(mainWrapper, 'slide-nav')
    } else {
      this.renderer.addClass(mainWrapper, 'slide-nav')
    }
  }

  // get isAdmin(): boolean {
  //   return this.user?.userRoles?.some((ur: any) => ur.role?.name === 'Admin') ?? false
  //   // return this.user?.role === 'Admin'
  // }

  get isOwner(): boolean {
    return this.user?.userRoles?.some((ur: any) => ur.role?.name === 'Business Owner')
      ? true
      : this.user?.employmentType === 'Business Owner'
        ? true
        : false
    // return this.user?.role === 'Admin'
  }

  get isAdmin(): boolean {
    return this.user?.userRoles?.some((ur: any) => ur?.role === 'Admin') ?? false
  }
  redirectToBilling() {
    this.router.navigate([eps.planAndBilling.planPath])
  }

  get showActivationLink(): boolean {
    return !this.isAdmin && this.isOwner && this.subscriptionStatus === SubscriptionStatus.Trialing
  }
  getNotifications(userId: string): Observable<AppNotification[]> {
    const search = ''
    const page = 1
    const limit = 5

    return this.notificationService.list(userId, search, page, limit).pipe(
      map(res =>
        (res.notifications || []).map((n: AppNotification) => ({
          ...n,
          createdAt: new Date(n.createdAt)
        }))
      ),
      catchError(err => {
        console.error('Failed to fetch notifications:', err)
        return of([])
      }),
      shareReplay(1)
    )
  }

  handleNotificationClick(notification: AppNotification) {
    console.log('Clicked notification:', notification)
  }

  // triggerAlert() {
  //   this.alertService.show('Your subscription is about to expire!')
  // }

  private triggerOnce(key: string, message: string, type: AlertType) {
    if (this.lastAlertKey === key) return
    this.lastAlertKey = key

    this.alertService.show(message, type)
  }
}
